import React, { useState, useRef, useEffect } from "react";

const ClientAutocomplete = ({
  selectedClient,
  clients = [],
  onChange,
  className = "",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false); // NOVO

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sincronizar inputValue com selectedClient
  useEffect(() => {
    if (isSelecting) {
      setIsSelecting(false);
      return;
    }

    if (selectedClient) {
      if (typeof selectedClient === "string") {
        const client = clients.find((c) => c.id === selectedClient);
        setInputValue(client ? client.label : "");
      } else if (selectedClient && selectedClient.label) {
        setInputValue(selectedClient.label);
      }
    } else {
      setInputValue("");
    }
  }, [selectedClient, clients]);

  // Resetar focusedIndex quando filteredClients mudar
  useEffect(() => {
    setFocusedIndex(-1);
  }, [filteredClients]);

  const filterClients = (searchText) => {
    if (!searchText || !Array.isArray(clients)) return clients;

    const searchTerms = removeAccents(searchText.toLowerCase()).split(/\s+/);

    return clients
      .sort((a, b) => {
        const aStartsWith = removeAccents(a.label.toLowerCase()).startsWith(
          searchTerms[0],
        );
        const bStartsWith = removeAccents(b.label.toLowerCase()).startsWith(
          searchTerms[0],
        );
        if (aStartsWith && !bStartsWith) return -1;
        if (!bStartsWith && aStartsWith) return 1;
        return a.label.localeCompare(b.label);
      })
      .filter((client) => {
        const normalizedName = removeAccents(client.label.toLowerCase());
        const nameWords = normalizedName.split(/\s+/);

        return searchTerms.every((term) =>
          nameWords.some((word) => word.includes(term)),
        );
      });
  };

  const removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  // MODIFICADO - remover o bloco else que chamava onChange
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);

    const filtered = filterClients(value);
    setFilteredClients(filtered);

    if (!value) {
      onChange && onChange(null);
    }
  };

  // MODIFICADO - adicionar setIsSelecting(true)
  const handleSelectClient = (client) => {
    if (!client) return;

    setIsSelecting(true);
    setInputValue(client.label);
    setShowSuggestions(false);
    setFocusedIndex(-1);
    onChange && onChange(client);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || !filteredClients.length) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev < filteredClients.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredClients.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && filteredClients[focusedIndex]) {
          handleSelectClient(filteredClients[focusedIndex]);
        } else if (filteredClients.length === 1) {
          handleSelectClient(filteredClients[0]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowSuggestions(false);
        setFocusedIndex(-1);
        if (selectedClient) {
          const client =
            typeof selectedClient === "string"
              ? clients.find((c) => c.id === selectedClient)
              : selectedClient;
          setInputValue(client ? client.label : "");
        } else {
          setInputValue("");
        }
        break;
      case "Tab":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    const filtered = filterClients(inputValue);
    setFilteredClients(filtered);
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(document.activeElement)
      ) {
        setShowSuggestions(false);
        setFocusedIndex(-1);

        if (selectedClient) {
          const client =
            typeof selectedClient === "string"
              ? clients.find((c) => c.id === selectedClient)
              : selectedClient;
          if (client && inputValue !== client.label) {
            setInputValue(client.label);
          }
        } else if (
          inputValue &&
          !filteredClients.find((c) => c.label === inputValue)
        ) {
          setInputValue("");
          onChange && onChange(null);
        }
      }
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const shouldShowSuggestions =
    showSuggestions &&
    Array.isArray(filteredClients) &&
    filteredClients.length > 0;

  return (
    <div ref={wrapperRef} className={`mui-style-autocomplete ${className}`}>
      <div className={`mui-style-input-container`}>
        <input
          ref={inputRef}
          type="text"
          className="mui-style-input"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Selecione um cliente"
          autoComplete="off"
          style={{ border: "none" }}
        />
      </div>

      {shouldShowSuggestions && (
        <div className="mui-style-suggestions">
          {filteredClients.map((client, index) => {
            const sameNameClients = filteredClients.filter(
              (c) => c.label === client.label,
            );
            const isDuplicate = sameNameClients.length > 1;

            return (
              <div
                key={`${client.id}-${index}`}
                className={`mui-style-suggestion ${
                  focusedIndex === index ? "focused" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={() => handleSelectClient(client)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                <div>
                  {client.label}
                  {isDuplicate && (
                    <span className="mui-style-client-id">
                      ID: #{client.id.slice(-4)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClientAutocomplete;
